// @flow

/**
 * Module dependencies.
 */

import { branch } from 'core/utils/branch';
import {
  colors,
  defaultTransition,
  keyframes,
  media,
  setStylesProp,
  states,
  units
} from 'client/styles';

import { ifProp } from 'styled-tools';
import { position, size } from 'polished';
import styled from 'styled-components';

/**
 * Export `FormGroup` component.
 */

export const FormGroup = styled.div`
  padding-top: ${units(3)};
  position: relative;
  text-align: left;

  &:after,
  &:before {
    ${position('absolute', null, '0', '0', '0')};
    ${size('1px', '100%')};

    content: '';
    display: block;
  }

  &:before {
    background-color: ${colors.shadesOfBlue.regentGray};
  }

  &:after {
    background-color: ${colors.brandPrimary};
    transform-origin: center;
    transform: scaleX(${ifProp('hasFocus', 1, 0)});
    transition: transform ${defaultTransition};
  }

  ${({ themeColor }) => branch(themeColor, `
    &:after {
      background: ${themeColor};
    }
  `)}

  ${({ hasError, hasFocus }) => branch(hasError, `
    ${branch(!hasFocus, `animation: ${keyframes.shake} .7s 1 linear;`)}

    &:before {
      background: ${colors.red} !important;
    }
  `)}
`;

/**
 * Export `Label` component.
 */

export const Label = styled.label`
  display: inline-block;
  left: ${units(1)};
  max-width: 100%;
  pointer-events: none;
  pointer-events: none;
  position: absolute;
  top: 1.6em;
  transition: all ${defaultTransition};

  ${({ active, hasFocus, themeColor }) => `
    color: ${hasFocus ? themeColor || colors.brandPrimary : colors.shadesOfBlue.regentGray};
    font-size: ${active ? 11 : 20}px;
    font-weight: ${active ? 700 : 400};
    transform: translateY(${active ? -1 : 0}em);
  `};

  ${media.min.xs`
    left: ${units(2)};
  `}
`;

/**
 * Export `FormControl` component.
 */

export const FormControl = styled.input`
  border: 0;
  display: block;
  font-size: 20px;
  line-height: 27px;
  padding: ${units(1)};
  width: 100%;

  ${({ themeColor }) => `
    color: ${themeColor || colors.shadesOfBlue.oxfordBlue};
  `}

  ${states.interaction`
    outline: none;
  `};

  ${media.min.xs`
    padding-left: ${units(2)};
    padding-right: ${units(2)};
  `}
`;

/**
 * Export `makeFormControl` generates a styled
 * component based on an element. It also can receive a
 * `styles` props that can be a funcion or a string.
 */

export function makeFormControl(element: string = 'input'): Function {
  return styled(element)`
    appearance: none;
    background: transparent;
    border: 0;
    color: ${colors.shadesOfBlue.oxfordBlue};
    display: block;
    font-size: 20px;
    line-height: 27px;
    padding: ${units(1)};
    width: 100%;

    ${states.interaction`
      outline: none;
    `};

    ${media.min.xs`
      padding-left: ${units(2)};
      padding-right: ${units(2)};
    `}

    ${setStylesProp};
  `;
}



// WEBPACK FOOTER //
// ../src/client/components/core/forms/styles.js